<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div v-if="!company.data.address" class="alert alert-warning">
        <strong>Warning!</strong> The subscriber has no address.
      </div>

      <form @submit.prevent="submit" class="form-horizontal">
        <div class="row">
          <div class="col-xs-6">
            <help-text>
              <h3>
                Create Subscription
              </h3>

              <p>
                Enter the portal that the subscription subscribes to, and select a subscription type.
              </p>

              <p>
                You may also attach a subscription period to the subscription. The subscription will be
                active during the period selected here.
              </p>

              <p>
                You may create more that one period in order to split the invoice into multiple parts.
              </p>

              <p>
                If no periods are created, the subscription will count as a trial subscription. The
                subscription will also have trial status until the start of the first subscription
                period, if one is created.
              </p>

              <button
                  type="button"
                  class="btn btn-sm btn-black"
                  @click="addSubscriptionPeriod"
              >
                <i class="fa fa-plus"></i> Add Subscription Period
              </button>
            </help-text>
          </div>

          <div class="col-xs-18">
            <basic-information
                v-model="subscription"
                :portals="portals"
                :subscription-types="subscriptionTypes"
                :people="people"
                :meetings="meetings"
                :opportunities="opportunities"
                :errors="errors"
                @open-person-modal="openPersonModal"
            ></basic-information>

            <subscription-period
                v-for="(subscriptionPeriod, index) in subscription.subscription_periods"
                :key="index"
                :subscription-period="subscriptionPeriod"
                :subscription="subscription"
                :index="index"
                :errors="errors"
                @input="subscription.subscription_periods.splice(index, 1, $event)"
                @remove="subscription.subscription_periods.splice(index, 1)"
            ></subscription-period>

            <change-defaults
                v-model="subscription"
                :errors="errors"
            ></change-defaults>

            <advanced-setup
                v-model="subscription"
                :errors="errors"
                @open-person-modal="openPersonModal"
            ></advanced-setup>
          </div>
        </div>

        <hr class="panel-separator">

        <div class="row">
          <div class="col-xs-offset-6 col-xs-18">
            <button type="submit" class="btn btn-black" style="margin-bottom: 22px">
              <i class="fa fa-save"></i> Save
            </button>
          </div>
        </div>
      </form>
    </div>

    <create-person-modal :company="company.data.id" @created="personCreated"></create-person-modal>
  </div>
</template>

<script>
import http from '@/http'
import moment from 'moment'
import notify from '@/notify'
import store from '@/store'
import AdvancedSetup from '@/pages/Companies/Companies/Edit/Tabs/CreateSubscription/Panels/AdvancedSetup'
import BasicInformation from '@/pages/Companies/Companies/Edit/Tabs/CreateSubscription/Panels/BasicInformation'
import ChangeDefaults from '@/pages/Companies/Companies/Edit/Tabs/CreateSubscription/Panels/ChangeDefaults'
import CreatePersonModal from '@/pages/Companies/Companies/Edit/Modals/CreatePerson'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import SubscriptionPeriod from '@/pages/Companies/Companies/Edit/Tabs/CreateSubscription/Panels/SubscriptionPeriod'

export default {
  components: {
    AdvancedSetup,
    BasicInformation,
    ChangeDefaults,
    CreatePersonModal,
    HelpText,
    LoadingContainer,
    SubscriptionPeriod
  },

  mixins: [Errors, Load],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company'],

  data() {
    return {
      subscription: {
        has_automatic_renewal: true,
        is_free: false,
        user_id: store.user.id,
        renewal_responsible_id: store.user.id,
        subscription_periods: [],
        opportunity_id: this.getQueryParam('opportunity')
      },
      portals: null,
      people: null,
      opportunities: null,
      subscriptionTypes: null
    }
  },

  mounted() {
    this.load({
      portals: '/api/v2/portals',
      people: '/api/v1/companies/' + this.company.data.id + '/people',
      meetings: '/api/v2/meetings?filter[company_id]=' + this.company.data.id,
      opportunities: '/api/v2/opportunities?filter[company_id]=' + this.company.data.id,
      subscriptionTypes: '/api/v2/subscription-types',
    })
  },

  watch: {
    'subscription.portal_id'() {
      this.subscription.subscription_type_id = null
    },

    'subscription.subscription_type_id'(value) {
      if (!value) {
        return
      }

      let subscriptionType = this.subscriptionTypes.data.find(subscriptionType => {
        return subscriptionType.id === parseInt(value)
      })

      this.subscription.price = subscriptionType.attributes.price

      this.subscription.period = subscriptionType.attributes.period

      this.subscription.closes_after = subscriptionType.attributes.closes_after

      this.subscription.seats = subscriptionType.attributes.seats
    },

    'subscription.price'(value) {
      this.subscription.subscription_periods = this.subscription.subscription_periods.map(period => {
        return {...period, price: value}
      })
    },

    'subscription.period'(value) {
      this.subscription.subscription_periods = this.subscription.subscription_periods.map(period => {
        return {...period, ends_at: moment(period.starts_at).add(value, 'months').format('YYYY-MM-DD')}
      })
    },

    'subscription.closes_after'(value) {
      this.subscription.subscription_periods = this.subscription.subscription_periods.map(period => {
        return {...period, closes_at: moment(period.starts_at).add(value, 'days').format('YYYY-MM-DD')}
      })
    },

    'subscription.opportunity_id'(value) {
      if (!value) {
        return this.subscription.meeting_id = null
      }

      this.subscription.meeting_id = this.opportunities.data.find(opportunity => opportunity.id === parseInt(value)).attributes.meeting_id
    }
  },

  methods: {
    openPersonModal() {
      $('#create-person-modal').modal('show')
    },

    personCreated(person) {
      this.people.data.push(person)

      this.people.data.sort((a, b) => a.name.toLowerCase() >= b.name.toLowerCase() ? 1 : -1)

      this.$nextTick(() => {
        this.subscription.contact_person_id = person.id
      })
    },

    submit() {
      http.post('/api/v1/subscriptions', {
        ...this.subscription,
        subscriber: {id: this.company.data.id, type: this.company.data.type}
      })
          .then(() => {
            notify.success('The subscription was saved')

            this.$router.push('.')
          })
          .catch(error => {
            if (error.response.status === 400) {
              return notify.error(error.response.data.message)
            }

            throw error
          })
          .catch(this.catchValidationErrors)
    },

    addSubscriptionPeriod() {
      this.subscription.subscription_periods.push({
        price: this.subscription.price,
        starts_at: moment().format('YYYY-MM-DD'),
        ends_at: moment().add(this.subscription.period, 'months').format('YYYY-MM-DD'),
        closes_at: moment().add(this.subscription.closes_after, 'days').format('YYYY-MM-DD')
      })
    },

    getQueryParam(key, defaultValue = null) {
      return typeof this.$route.query[key] !== 'undefined' ? this.$route.query[key] : defaultValue
    }
  }
}
</script>
